import { html } from '../../html.js';
import type { VNode } from '../../html.js';
import type { DashboardActions, ThemeChoice } from '../store.js';

/**
 * The theme control, and the two functions that make it stick.
 *
 * THREE STATES, NOT TWO. A two-state toggle has to pick a side the first time
 * it is drawn, and whichever side it picks is wrong for half the people who
 * open it. `system` is the default and stamps NOTHING on the root element, so
 * the OS keeps deciding and a laptop that flips at sunset flips this page too;
 * `light` and `dark` stamp `data-theme` and win over the media query in both
 * directions, which is why tokens.css writes the light palette twice.
 *
 * THE FIRST PAINT IS THE POINT. The choice is read and stamped by a four-line
 * script in dashboard.html, before the stylesheet is asked for, because a dark
 * page that flashes white for one frame on every reload is the single most
 * noticeable defect a theme toggle can ship with. This module writes the same
 * key that script reads, and nothing else does.
 *
 * The control lives on the dashboard and the console and NOT on the marketing
 * site: those two are the surfaces that already require JavaScript, and a
 * toggle on a static page is either a flash of the wrong theme or a script the
 * page did not otherwise need.
 */

/** Read by the pre-paint script in dashboard.html. Changing it changes both. */
export const THEME_KEY = 'clawharbor-theme';

const CHOICES: readonly { value: ThemeChoice; label: string; hint: string }[] = [
  { value: 'system', label: 'System', hint: 'Follow the operating system.' },
  { value: 'light', label: 'Light', hint: 'Always the light theme.' },
  { value: 'dark', label: 'Dark', hint: 'Always the dark theme.' },
];

function isChoice(value: string | null): value is ThemeChoice {
  return value === 'system' || value === 'light' || value === 'dark';
}

/**
 * What was chosen last time, or `system`.
 *
 * Storage can throw outright — a browser set to block site data, a page opened
 * from `file://` — and a dashboard that fails to boot because it could not read
 * a cosmetic preference would be an absurd way to lose a management tool.
 */
export function storedTheme(): ThemeChoice {
  try {
    const raw = localStorage.getItem(THEME_KEY);
    return isChoice(raw) ? raw : 'system';
  } catch {
    return 'system';
  }
}

/**
 * Stamp the choice and remember it.
 *
 * `system` REMOVES the attribute rather than writing "system": tokens.css keys
 * its light palette off `prefers-color-scheme` guarded by
 * `:root:not([data-theme="light"])`, and an attribute with an unknown value
 * sitting there would be a third state neither block accounts for.
 */
export function applyTheme(choice: ThemeChoice): void {
  const root = document.documentElement;
  if (choice === 'system') root.removeAttribute('data-theme');
  else root.setAttribute('data-theme', choice);
  // `color-scheme` is what paints the scrollbars, the form controls and the
  // canvas behind the page; the attribute alone leaves those on the OS default.
  root.style.colorScheme = choice === 'system' ? 'dark light' : choice;
  try {
    localStorage.setItem(THEME_KEY, choice);
  } catch {
    // A preference that cannot be written is still applied for this page.
  }
}

/**
 * Three radios in a row, not a button that cycles.
 *
 * A cycling button cannot say what the next press will do, and with three
 * states nobody can predict it. A radiogroup announces all three options and
 * which one is current, and it is operable with the arrow keys for free.
 */
export function ThemeToggle(props: { theme: ThemeChoice; actions: DashboardActions }): VNode {
  return html`
    <div class="themeset" role="radiogroup" aria-label="Colour theme">
      ${CHOICES.map(
        (c) => html`<button
          type="button"
          class=${c.value === props.theme ? 'themeset__opt themeset__opt--on' : 'themeset__opt'}
          role="radio"
          aria-checked=${c.value === props.theme ? 'true' : 'false'}
          title=${c.hint}
          onClick=${() => props.actions.setTheme(c.value)}
        >
          ${c.label}
        </button>`,
      )}
    </div>
  `;
}
